import {
  localSelection,
  applyLocalPrice,
  syncLocalVariantOptions,
} from "./tc-local-variants.js?v=20261026-combinations-1";
import {
  initializeOptionThumbnails,
  syncLocalOptionThumbnails,
  syncServerOptionThumbnails,
} from "./tc-option-thumbnails.js?v=20261026-combinations-1";
import { Component } from "@theme/component";
import { morph, MORPH_OPTIONS } from "@theme/morph";
import { OverflowList } from "@theme/overflow-list";
import { getViewParameterValue, ResizeNotifier } from "@theme/utilities";
import { ProductSelectEvent } from "@shopify/events";
export default class VariantPicker extends Component {
  #pendingRequestUrl;
  #abortController;
  #checkedIndices = [];
  #radios = [];
  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());
  #onChange = (event) => this.variantChanged(event);
  connectedCallback() {
    (super.connectedCallback(),
      (this.#checkedIndices = []),
      (this.#radios = []),
      (this.refs.fieldsets || []).forEach((fieldset) => {
        const radios = Array.from(fieldset?.querySelectorAll("input") ?? []);
        this.#radios.push(radios);
        const initialCheckedIndex = radios.findIndex(
          (radio) => radio.dataset.currentChecked === "true",
        );
        this.#checkedIndices.push(
          initialCheckedIndex < 0 ? [] : [initialCheckedIndex],
        );
      }),
      this.addEventListener("change", this.#onChange),
      initializeOptionThumbnails(this),
      this.querySelector(".variant-option--swatches") &&
        this.#resizeObserver.observe(this));
    const resolution = syncLocalVariantOptions(this, true);
    syncLocalOptionThumbnails(this);
    if (resolution?.changed.length) {
      this.variantChanged({ target: resolution.changed[0] });
    }
  }
  disconnectedCallback() {
    (super.disconnectedCallback(),
      this.#abortController?.abort(),
      this.removeEventListener("change", this.#onChange),
      this.#resizeObserver.disconnect());
  }
  getLocalVariant() {
    return localSelection(this);
  }
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const selectedOption =
      event.target instanceof HTMLSelectElement
        ? event.target.options[event.target.selectedIndex]
        : event.target;
    if (!selectedOption?.dataset.optionValueId) return;
    this.updateSelectedOption(event.target);
    const resolution = syncLocalVariantOptions(this, true);
    const localVariant = this.getLocalVariant();
    applyLocalPrice(this, localVariant);
    if (localVariant?.image?.src) {
      const scope = this.closest("dialog") || this.closest(".shopify-section");
      for (const form of scope?.querySelectorAll("product-form-component") ||
        []) {
        if (String(form.dataset.productId) !== String(this.dataset.productId))
          continue;
        for (const button of form.querySelectorAll("add-to-cart-component")) {
          button.dataset.productVariantMedia = localVariant.image.src;
        }
      }
    }
    syncLocalOptionThumbnails(this);
    const isOnProductPage =
        this.dataset.templateProductMatch === "true" &&
        !event.target.closest("product-card") &&
        !event.target.closest("quick-add-dialog"),
      currentUrl = this.dataset.productUrl?.split("?")[0],
      newUrl = selectedOption.dataset.connectedProductUrl,
      loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl,
      isOnFeaturedProductSection = !!this.closest(
        "featured-product-information",
      ),
      morphElementSelector = loadsNewProduct
        ? "main"
        : isOnFeaturedProductSection
          ? "featured-product-information"
          : void 0,
      {
        optionValueId = "",
        variantId: serverVariantId = "",
        connectedProductUrl = "",
      } = selectedOption.dataset;
    // An option's server-rendered variant ID can still describe the previous
    // combination. The exact local match is authoritative for this selection.
    const variantId = resolution
      ? resolution.variant
        ? String(resolution.variant.id)
        : ""
      : localVariant
        ? String(localVariant.id)
        : serverVariantId;
    // Keep the native product-select event and server reconciliation. Gallery,
    // form and sticky bar read getLocalVariant() synchronously in that event.
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      detail: { optionValueId, variantId, connectedProductUrl },
    });
    const url = new URL(window.location.href);
    (isOnProductPage &&
      (variantId
        ? url.searchParams.set("variant", variantId)
        : url.searchParams.delete("variant")),
      loadsNewProduct && (url.pathname = newUrl),
      url.href !== window.location.href &&
        history.replaceState(history.state, "", url.toString()));
  }
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldset = (this.refs.fieldsets || [])[fieldsetIndex],
      checkedIndices = this.#checkedIndices[fieldsetIndex],
      radios = this.#radios[fieldsetIndex];
    if (!radios || !checkedIndices || !fieldset) return null;
    const [currentIndex, previousIndex] = checkedIndices;
    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth:
        currentIndex !== void 0
          ? radios[currentIndex]?.parentElement?.offsetWidth
          : void 0,
      previousWidth:
        previousIndex !== void 0
          ? radios[previousIndex]?.parentElement?.offsetWidth
          : void 0,
    };
  }
  #applyFieldsetMeasurements({
    fieldset,
    currentWidth,
    previousWidth,
    currentIndex,
    previousIndex,
  }) {
    (currentWidth
      ? fieldset.style.setProperty("--pill-width-current", `${currentWidth}px`)
      : currentIndex !== void 0 &&
        fieldset.style.removeProperty("--pill-width-current"),
      previousWidth
        ? fieldset.style.setProperty(
            "--pill-width-previous",
            `${previousWidth}px`,
          )
        : previousIndex !== void 0 &&
          fieldset.style.removeProperty("--pill-width-previous"));
  }
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;
    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    measurements && this.#applyFieldsetMeasurements(measurements);
  }
  updateSelectedOption(target) {
    if (typeof target == "string") {
      const targetElement = this.querySelector(
        `[data-option-value-id="${target}"]`,
      );
      if (!targetElement) throw new Error("Target element not found");
      target = targetElement;
    }
    if (target instanceof HTMLInputElement) {
      const fieldset = target.closest("fieldset");
      if (fieldset) {
        const radios = [...fieldset.querySelectorAll('input[type="radio"]')];
        for (const radio of radios) {
          const checked = radio === target;
          ((radio.checked = checked),
            radio.toggleAttribute("checked", checked),
            (radio.dataset.currentChecked = String(checked)),
            (radio.dataset.previousChecked = "false"));
        }
      }
      target.checked = !0;
    }
    if (target instanceof HTMLSelectElement) {
      const newValue = target.value,
        newSelectedOption = Array.from(target.options).find(
          (option) => option.value === newValue,
        );
      if (!newSelectedOption) throw new Error("Option not found");
      for (const option of target.options) option.removeAttribute("selected");
      newSelectedOption.setAttribute("selected", "selected");
    }
  }
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = [],
  ) {
    const productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;
    if (!productUrl) throw new Error("Missing product URL");
    this.#pendingRequestUrl = productUrl;
    const url = new URL(productUrl, window.location.origin);
    (url.searchParams.delete("variant"),
      url.searchParams.delete("option_values"),
      url.searchParams.delete("section_id"));
    const view = getViewParameterValue();
    view && url.searchParams.set("view", view);
    const values =
      source === "product-card"
        ? sourceSelectedOptionsValues.length
          ? sourceSelectedOptionsValues
          : [selectedOption.dataset.optionValueId]
        : this.selectedOptionsValues;
    values.length && url.searchParams.set("option_values", values.join(","));
    const fullMain =
        url.pathname !==
          new URL(this.dataset.productUrl || productUrl, window.location.origin)
            .pathname &&
        this.dataset.templateProductMatch === "true" &&
        !this.closest("product-card, quick-add-dialog"),
      featured = this.closest("featured-product-information"),
      sectionId = this.closest(
        "quick-add-component, swatches-variant-picker-component",
      )
        ? "section-rendering-product-card"
        : featured?.id || this.dataset.sectionId;
    return (
      sectionId && !fullMain && url.searchParams.set("section_id", sectionId),
      url.toString()
    );
  }
  fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {} } = {}) {
    const { optionValueId = "", variantId, connectedProductUrl = "" } = detail;
    (this.#abortController?.abort(),
      (this.#abortController = new AbortController()));
    const controller = this.#abortController,
      deferredEventPromise = ProductSelectEvent.createPromise(),
      selectedOptions = this.getAllSelectedOptions();
    (this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? "",
          title: this.dataset.productTitle ?? "",
          handle: this.dataset.productHandle ?? "",
        },
        selectedOptions,
        detail: { optionValueId, variantId, connectedProductUrl },
        promise: deferredEventPromise.promise,
      }),
    ),
      fetch(requestUrl, { signal: controller.signal })
        .then((response) => {
          if (!response.ok)
            throw new Error(`Variant request failed: ${response.status}`);
          return response.text();
        })
        .then((responseText) => {
          if (controller.signal.aborted || controller !== this.#abortController)
            throw new DOMException("Superseded variant request", "AbortError");
          this.#pendingRequestUrl = void 0;
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html",
          );
          html.querySelector("overflow-list[defer]")?.removeAttribute("defer");
          const variantPickerJsonScript = this.findUpdatedPicker(
              html,
            )?.querySelector('script[type="application/json"]'),
            textContent = variantPickerJsonScript?.textContent;
          if (!textContent) {
            deferredEventPromise.resolve({
              variant: null,
              detail: {
                html,
                productId: this.dataset.productId ?? "",
                sourceId: this.selectedOptionId,
                resource: null,
              },
            });
            return;
          }
          let newProduct;
          if (morphElementSelector === "main")
            this.updateMain(html.cloneNode(!0));
          else if (morphElementSelector)
            this.updateElement(html.cloneNode(!0), morphElementSelector);
          else {
            const { overflowList } = this.refs,
              wasSwatchesExpanded =
                overflowList instanceof OverflowList &&
                overflowList.getAttribute("disabled") === "true";
            if (
              ((newProduct = this.updateVariantPicker(html.cloneNode(!0))),
              wasSwatchesExpanded)
            ) {
              const overflowListAfterMorph = overflowList;
              overflowListAfterMorph instanceof OverflowList &&
                overflowListAfterMorph.showAll();
            }
          }
          if (this.selectedOptionId) {
            const variantData = JSON.parse(textContent);
            if (variantData && typeof variantData == "object") {
              const productViewAttr = variantPickerJsonScript
                ?.closest("[view-event-payload]")
                ?.getAttribute("view-event-payload")
                ?.trim();
              deferredEventPromise.resolve({
                variant:
                  (productViewAttr && JSON.parse(productViewAttr))?.product
                    ?.selectedVariant ?? null,
                detail: {
                  html,
                  productId: this.dataset.productId ?? "",
                  newProduct,
                  sourceId: this.selectedOptionId,
                  resource: variantData,
                },
              });
              return;
            }
          }
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? "",
              newProduct,
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
        })
        .catch((error) => {
          deferredEventPromise.reject(error);
          if (error.name !== "AbortError") console.error(error);
        }));
  }
  findUpdatedPicker(html) {
    const pickers = [...html.querySelectorAll(this.tagName.toLowerCase())];
    return (
      pickers.find(
        (picker) =>
          picker.dataset.blockId === this.dataset.blockId &&
          picker.dataset.sectionId === this.dataset.sectionId,
      ) ||
      pickers.find(
        (picker) => picker.dataset.productId === this.dataset.productId,
      ) ||
      pickers[0]
    );
  }
  updateVariantPicker(newHtml) {
    let newProduct;
    const newVariantPickerSource = this.findUpdatedPicker(newHtml);
    if (!newVariantPickerSource)
      throw new Error("No new variant picker source found");
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId,
        newProductUrl = newVariantPickerSource.dataset.productUrl;
      (newProductId &&
        newProductUrl &&
        this.dataset.productId !== newProductId &&
        (newProduct = { id: newProductId, url: newProductUrl }),
        (this.dataset.productId = newProductId),
        (this.dataset.productUrl = newProductUrl));
    }
    const currentOptionNodes = [
      ...this.querySelectorAll("[data-option-value-id]"),
    ];
    const nextOptionNodes = [
      ...newVariantPickerSource.querySelectorAll("[data-option-value-id]"),
    ];
    const sameOptions =
      currentOptionNodes.length === nextOptionNodes.length &&
      currentOptionNodes.every((option, index) => {
        const next = nextOptionNodes[index];
        return (
          option.tagName === next.tagName &&
          option.value === next.value &&
          option.dataset.optionValueId === next.dataset.optionValueId
        );
      });
    if (
      newVariantPickerSource.dataset.productId === this.dataset.productId &&
      !newProduct &&
      sameOptions
    ) {
      const sourceOptions = new Map(
        [
          ...newVariantPickerSource.querySelectorAll("[data-option-value-id]"),
        ].map((option) => [option.dataset.optionValueId, option]),
      );
      for (const option of this.querySelectorAll("[data-option-value-id]")) {
        const source = sourceOptions.get(option.dataset.optionValueId);
        if (source) {
          for (const name of [
            "data-variant-id",
            "data-option-available",
            "data-option-exists",
            "data-connected-product-url",
            "aria-disabled",
          ])
            source.hasAttribute(name)
              ? option.setAttribute(name, source.getAttribute(name))
              : option.removeAttribute(name);
          option.disabled = source.disabled;
          const item =
            option.tagName === "OPTION" ? option : option.closest("label");
          const sourceItem =
            source.tagName === "OPTION" ? source : source.closest("label");
          if (item && sourceItem) item.hidden = sourceItem.hidden;
        }
      }
      syncServerOptionThumbnails(this, newVariantPickerSource);
      const scripts = [
          ...this.querySelectorAll('script[type="application/json"]'),
        ],
        sourceScripts = [
          ...newVariantPickerSource.querySelectorAll(
            'script[type="application/json"]',
          ),
        ];
      return (
        scripts.forEach((script, index) => {
          sourceScripts[index] &&
            (script.textContent = sourceScripts[index].textContent);
        }),
        syncLocalVariantOptions(this),
        syncLocalOptionThumbnails(this),
        newProduct
      );
    }
    return (
      morph(this, newVariantPickerSource, {
        ...MORPH_OPTIONS,
        getNodeKey: (node) =>
          node instanceof HTMLElement ? node.dataset.key : void 0,
      }),
      this.updateVariantPickerCss(),
      newProduct
    );
  }
  updateVariantPickerCss() {
    const measurements = (this.refs.fieldsets || [])
      .map((_, index) => this.#getFieldsetMeasurements(index))
      .filter((m) => m !== null);
    for (const measurement of measurements)
      this.#applyFieldsetMeasurements(measurement);
  }
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector),
      newElement = newHtml.querySelector(elementSelector);
    if (!element || !newElement)
      throw new Error(`No new element source found for ${elementSelector}`);
    morph(element, newElement);
  }
  updateMain(newHtml) {
    const main = document.querySelector("main"),
      newMain = newHtml.querySelector("main");
    if (!main || !newMain) throw new Error("No new main source found");
    morph(main, newMain);
  }
  get selectedOption() {
    const selectedOption = this.querySelector(
      "select option:checked, fieldset input:checked",
    );
    if (
      selectedOption instanceof HTMLInputElement ||
      selectedOption instanceof HTMLOptionElement
    )
      return selectedOption;
  }
  getAllSelectedOptions() {
    // Keep product option order when a picker mixes radios and dropdowns.
    return [
      ...this.querySelectorAll("select option:checked, fieldset input:checked"),
    ]
      .filter((option) => option.dataset.optionName)
      .map((option) => ({
        name: option.dataset.optionName,
        value: option.value,
      }));
  }
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return;
    const { optionValueId } = selectedOption.dataset;
    if (!optionValueId) throw new Error("No option value ID found");
    return optionValueId;
  }
  get selectedOptionsValues() {
    return Array.from(
      this.querySelectorAll("select option:checked, fieldset input:checked"),
    ).map((option) => {
      const { optionValueId } = option.dataset;
      if (!optionValueId) throw new Error("No option value ID found");
      return optionValueId;
    });
  }
}
customElements.get("variant-picker") ||
  customElements.define("variant-picker", VariantPicker);
